import { ClickableAITag } from 'component/aitag';
import { Center, HeaderBox, Pad, PadBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { ActionEdit, ActionReply, BaseComments, BasicComments, CommentsInput, ComposerScreen, commentTable } from 'component/comment';
import { spacings } from 'component/constants';
import { FormField } from 'component/form';
import { ConversationScreen } from 'component/screen';
import { Heading, Paragraph, TextField, UtilityText } from 'component/text';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { TopicConfig } from 'structure/topic';
import { endAtProperty } from 'structure/topic';
import { Catcher } from 'system/catcher';
import { useIsAdmin } from 'util/admin';
import { useCollection, useDatastore, useGlobalProperty } from 'util/datastore';
import { useConfig } from 'util/features';
import { declareGlobalProperty, t } from 'util/stdtypes';
import { toBool } from 'util/util';

export const TopicUserQuestionsFeature = {
    key: 'topicuserquestions',
    name: 'Community Questions',
    dev: true,
    providesConfig: {},
    subscreens: {
        allQuestions: () => <AllQuestions />,
        composer: ({ commentKey, about }) => <ComposerScreen about={about} commentKey={commentKey} intro={<QuestionHeader />} />,
        conversationStarterAdmin: () => <ConversationStarterAdminScreen />,
    },
    receivesConfigDefaults: {
        commentActions: [ActionReply],
        commentRightActions: [ActionEdit],
        commentTopWidgets: [],
        commentAboveWidgets: [],
        replyAboveWidgets: [],
        commentAllowEmpty: false,
        commentEditBottomWidgets: [],
        commentEditTopWidgets: [],
        commentPostBlockers: [],
        commentInputPlaceholder: 'Ask us a question...',
        commentReplyPlaceholder: 'Reply to {authorName}...',
        commentInputLoginAction: 'answer',
        commentPostCheckers: [],
        commentFilters: [],
        commentRankers: [],
        replyFilters: [],
        pageTopWidgets: [],
        topBanners: [],
        composerTopBanners: [],
        composerSubtitle: () => 'Public response',
        poll: null,
        noCommentsMessage: t('Be the first to share your thoughts.'),
        noMoreCommentsMessage: t('No more questions'),
        PollWidget: null,
        visualizationType: 'percent',
        teaser: null,
        adminScreen: null,
        commentLimit: () => 4,
    },
};

const conversationStarterProperty = declareGlobalProperty<string>('conversationStarter');

export const TopicUserQuestionCallToActionFeature = {
    key: 'topicuserctaquestions',
    name: 'Community Questions Call to Action',
    dev: true,
    providesConfig: {
        topicContentWidgets: [TopicCallToAction],
    },
};

export const TopicOfTheMomentCallToActionFeature = {
    key: 'tomuserctaquestions',
    name: 'Topic of the moment Call to Action',
    dev: true,
    providesConfig: {
        topicTopWidgets: [TopicOfTheMomentCallToAction],
        topicHeaderBottomWidgets: [TopicClosed],
    },
};

export const TopicPopularQuestionsFeature = {
    key: 'topicpopularquestions',
    name: 'Popular Questions',
    dev: true,
    providesConfig: {
        topicContentWidgets: [TopicQuestions],
        commentsTopLeftWidgets: [() => <Heading label={t('Popular questions')} />],
        commentsBottomWidgets: [PopularCommentsCallToAction],
    },
};

export const TopicAIModerationTagFeature = {
    key: 'topicaimoderationtag',
    name: 'AI Moderation Tag',
    dev: true,
    providesConfig: {
        commentsTopRightWidgets: [() => <ClickableAITag />],
    },
};

function TopicOfTheMomentCallToAction() {
    const endAt = useGlobalProperty(endAtProperty);
    const live = toBool(endAt && endAt > new Date().toISOString());

    if (!live) {
        return null;
    }

    return <TopicCallToAction />;
}

function TopicClosed() {
    const { readOnly, composerPreview } = useConfig<TopicConfig>();
    if (readOnly && composerPreview) {
        return React.createElement(composerPreview);
    }
    return null;
}

function TopicCallToAction() {
    const { readOnly } = useConfig<TopicConfig>();

    if (readOnly) return null;

    return (
        <PadBox horiz={spacings.lg} top={spacings.xl2}>
            <CommentsInput />
            <Pad size={spacings.sm} />
        </PadBox>
    );
}

export type PopularCommentsProps = { about?: string | null; overrideCommentLimit?: boolean };

export function PopularComments({ about = null, overrideCommentLimit = false }: PopularCommentsProps) {
    const { commentsTopLeftWidgets, commentsTopRightWidgets, commentsBottomWidgets } = useConfig<TopicConfig>();
    const comments = useCollection(commentTable, { filter: { about, replyTo: null, deleted: null }, sortBy: 'time', reverse: true });
    const s = BasicCommentsStyles;

    return (
        <View>
            <PadBox vert={16}>
                <View style={s.container}>
                    <View style={s.leftContainer}>
                        {commentsTopLeftWidgets?.map((Widget, i) => (
                            <Catcher key={i}>{React.createElement(Widget as any, { comments })}</Catcher>
                        ))}
                    </View>
                    <View style={s.rightContainer}>
                        {commentsTopRightWidgets?.map((Widget, i) => (
                            <Catcher key={i}>{React.createElement(Widget as any, { comments })}</Catcher>
                        ))}
                    </View>
                </View>
            </PadBox>
            <BaseComments about={about} overrideCommentLimit={overrideCommentLimit} />
            {commentsBottomWidgets?.map((Widget, i) => (
                <Catcher key={i}>{React.createElement(Widget as any, { comments })}</Catcher>
            ))}
        </View>
    );
}

function PopularCommentsCallToAction() {
    const datastore = useDatastore();
    const comments = useCollection(commentTable, { filter: { about: null, replyTo: null, deleted: null }, sortBy: 'time' });

    if (comments?.length < 4) return null;

    return (
        <>
            <Pad size={spacings.lg} />
            <Center>
                <CTAButton
                    label={t('View all questions')}
                    onPress={datastore.needsLogin(() => datastore.pushSubscreen('allQuestions', {}))}
                    type='primary'
                />
            </Center>
        </>
    );
}

const BasicCommentsStyles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    leftContainer: {
        flex: 1,
        alignItems: 'flex-start',
    },
    rightContainer: {
        flex: 1,
        alignItems: 'flex-end',
    },
});

function TopicQuestions() {
    const datastore = useDatastore();
    const comments = useCollection(commentTable, { filter: { about: null, replyTo: null, deleted: null }, sortBy: 'time' });

    return (
        <PadBox horiz={spacings.lg} vert={spacings.xl2}>
            <Catcher>
                <PopularComments />
            </Catcher>
        </PadBox>
    );
}

function QuestionHeader() {
    const conversationStarter = useGlobalProperty(conversationStarterProperty);
    const { introHeaderTopRightWidgets } = useConfig<TopicConfig>();

    return (
        <HeaderBox horiz={spacings.lg} vert={spacings.xl2}>
            <View
                style={{
                    flex: 1,
                    alignItems: 'flex-end',
                    paddingBottom: 12,
                }}
            >
                {introHeaderTopRightWidgets?.map((Widget, i) => (
                    <Catcher key={i}>
                        <Widget key={i} />
                    </Catcher>
                ))}
            </View>
            <Heading text={conversationStarter ?? ''} type='small' weight='medium' />
        </HeaderBox>
    );
}

function AllQuestions() {
    return (
        <ConversationScreen>
            <PadBox horiz={spacings.lg} vert={spacings.xl2}>
                <Catcher>
                    <BasicComments canPost={false} overrideCommentLimit />
                </Catcher>
            </PadBox>
        </ConversationScreen>
    );
}

function ConversationStarterAdminScreen() {
    const datastore = useDatastore();
    const baseConversationStarter = useGlobalProperty(conversationStarterProperty);
    const [conversationStarter, setConversationStarter] = useState(baseConversationStarter);
    const isAdmin = useIsAdmin();

    const updateConversationStarter = () => {
        datastore.setGlobalProperty(conversationStarterProperty, conversationStarter);
        datastore.goBack();
    };

    if (!isAdmin) {
        return <UtilityText text={'Access denied'} />;
    }

    return (
        <ConversationScreen>
            <PadBox horiz={spacings.lg} vert={spacings.xl2}>
                <Heading label={t('Edit conversation starter (admin)')} type='large' weight='medium' />
                <PadBox vert={spacings.lg}>
                    <FormField label={t('Conversation starter')}>
                        <TextField
                            multiline={false}
                            onChange={setConversationStarter}
                            placeholderLabel={t('Conversation starter')}
                            value={conversationStarter}
                        />
                    </FormField>
                </PadBox>
                <CTAButton label={t('Update')} onPress={updateConversationStarter} />
            </PadBox>
        </ConversationScreen>
    );
}
